// Trailmap L2 Drip 3 — pure geometry helpers for in-canvas expansion. No React
// / ReactFlow runtime imports → api-free, safe in CRA-jest and unit-testable.

// Oldest expanded MAP id when expanding a new map would exceed the WebGL cap;
// else null. Set insertion order = expansion order (oldest first), so a
// re-expanded map moves to the tail and the truly-oldest map is evicted.
export function selectMapToEvict(
  expandedIds: ReadonlySet<string>,
  typeOf: (id: string) => string | undefined,
  mapTypes: ReadonlySet<string>,
  cap: number,
): string | null {
  const maps = [...expandedIds].filter((id) => mapTypes.has(typeOf(id) ?? ''));
  return maps.length >= cap ? maps[0] : null;
}

export interface Viewport { x: number; y: number; zoom: number; }
export interface FlowRect { x: number; y: number; w: number; h: number; }

// One-shot PAN (zoom unchanged) so an off-screen tile comes into view with
// padding; null if it already fits. Screen = flow*zoom + viewport.{x,y}.
//
// When the tile FITS the viewport, align the overflowing edge (minimal pan to
// reveal the whole tile). When the tile is BIGGER than the viewport, pin the
// NEAR edge (top / left) — the operator reads top-down / left-first, so we
// reveal the top-left corner rather than centering (spec §7).
export function computePanToFit(
  vp: Viewport,
  tile: FlowRect,
  vpPx: { width: number; height: number },
  pad: number,
): Viewport | null {
  const w = tile.w * vp.zoom;
  const h = tile.h * vp.zoom;
  const left = tile.x * vp.zoom + vp.x;
  const top = tile.y * vp.zoom + vp.y;
  const right = left + w;
  const bottom = top + h;

  let dx = 0;
  if (left < pad) dx = pad - left;
  else if (right > vpPx.width - pad) {
    dx = w <= vpPx.width - 2 * pad ? (vpPx.width - pad) - right : pad - left;
  }

  let dy = 0;
  if (top < pad) dy = pad - top;
  else if (bottom > vpPx.height - pad) {
    dy = h <= vpPx.height - 2 * pad ? (vpPx.height - pad) - bottom : pad - top;
  }

  if (dx === 0 && dy === 0) return null;
  return { x: vp.x + dx, y: vp.y + dy, zoom: vp.zoom };
}
